import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["prefixWithSelectedItemsPlaceholder", "selectedItemsPlaceholder", "showPreview", "showTags", "value", "valueRendererPrefix", "onChange", "onPreviewVoice"];
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import { DetailsByVoice, Voices } from './constants';
import Samples from './samples';
import { PlayerPauseIcon, PlayerPlayIcon } from 'foundations-assets';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { Select } from 'foundations-components/components/Select';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const PlayButtonWrapper = styled.div.withConfig({
  displayName: "VoiceSelectWithPreview__PlayButtonWrapper",
  componentId: "sc-9uwien-0"
})(["position:relative;display:flex;"]);
const ProgressAnimation = styled.svg.withConfig({
  displayName: "VoiceSelectWithPreview__ProgressAnimation",
  componentId: "sc-9uwien-1"
})(["position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;"]);
const Progress = styled.circle.withConfig({
  displayName: "VoiceSelectWithPreview__Progress",
  componentId: "sc-9uwien-2"
})(["@keyframes grow{100%{stroke-dashoffset:0;}}stroke:#00a4bd;stroke-dasharray:296 296;stroke-dashoffset:296;animation:", ";"], props => props.isPlaying && 'grow 5s ease-out forwards');
const VoicePreviewButton = ({
  voice,
  playingVoice,
  onClickPlay
}) => {
  return /*#__PURE__*/_jsxs(PlayButtonWrapper, {
    "aria-label": I18n.text('audioComponents.preview.tooltip'),
    onClick: e => onClickPlay(e, voice),
    children: [/*#__PURE__*/_jsx(IconCircle, {
      icon: playingVoice === voice ? PlayerPauseIcon : PlayerPlayIcon,
      padding: "100",
      size: "100",
      borderColor: "fill-tertiary-pressed",
      borderWidth: "100",
      borderStyle: "solid"
    }), /*#__PURE__*/_jsx(ProgressAnimation, {
      className: "viewbox",
      viewBox: "0 0 100 100",
      children: /*#__PURE__*/_jsx(Progress, {
        cx: "50",
        cy: "50",
        r: "45",
        transform: "rotate(-90 50 50)",
        stroke: "#00a4bd",
        strokeWidth: "5",
        fill: "none",
        isPlaying: voice === playingVoice
      })
    })]
  });
};

// todo cleanup - these are unused and temporarily left as to not break builds as we migrate to Trelils Select

const VoiceSelectWithPreview = _ref => {
  let {
      prefixWithSelectedItemsPlaceholder,
      selectedItemsPlaceholder = I18n.text('audioComponents.voiceSelect.placeholder'),
      showPreview = true,
      showTags,
      value,
      valueRendererPrefix,
      onChange,
      onPreviewVoice
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const audioRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [playingVoice, setPlayingVoice] = useState();
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('ended', () => setPlayingVoice(undefined));
    }
  }, []);
  const handleSelectedOptionChange = useCallback(option => {
    onChange(syntheticEvent(option.value));
  }, [onChange]);
  const handleClickPlay = useCallback((e, voice) => {
    e.stopPropagation();
    e.preventDefault();
    if (!audioRef.current) {
      return;
    }
    if (voice === playingVoice && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlayingVoice(undefined);
      return;
    }
    audioRef.current.src = Samples[voice];
    audioRef.current.play().catch(console.error);
    setPlayingVoice(voice);
    onPreviewVoice === null || onPreviewVoice === void 0 || onPreviewVoice(voice);
  }, [audioRef, playingVoice, onPreviewVoice]);
  const handleOpenChange = open => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      setPlayingVoice(undefined);
    }
    setIsOpen(open);
  };
  const options = useMemo(() => Object.values(Voices).map(voice => {
    const voiceName = valueRendererPrefix ? `${valueRendererPrefix} ${I18n.text(`audioComponents.voices.${voice.toLowerCase()}`)}` : I18n.text(`audioComponents.voices.${voice.toLowerCase()}`);
    if (!showPreview && !showTags) {
      return {
        text: voiceName,
        value: voice
      };
    }
    const voiceTags = DetailsByVoice[voice].tags;
    // todo use ReactNode for supportText with actual Tags and proper alignment with voice name https://git.hubteam.com/HubSpot/trellis-forum/discussions/73
    // for now using a prefix of 5 U+2000 "en quad" chars as a workaround, borrowed from https://en.wikipedia.org/wiki/Whitespace_character
    // eslint-disable-next-line no-irregular-whitespace
    const voiceTagString = `     ${voiceTags.map(tag => I18n.text(`audioComponents.voiceTags.${tag}`)).join(', ')}`;
    return {
      text: voiceName,
      value: voice,
      supportText: showTags ? voiceTagString : undefined,
      indicator: showPreview ? /*#__PURE__*/_jsx(VoicePreviewButton, {
        voice: voice,
        playingVoice: playingVoice,
        onClickPlay: handleClickPlay
      }) : undefined
    };
  }), [playingVoice, showPreview, showTags, valueRendererPrefix, handleClickPlay]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showPreview && /*#__PURE__*/_jsx("audio", {
      controls: false,
      ref: audioRef,
      src: value ? Samples[value] : undefined
    }), /*#__PURE__*/_jsx(Select, Object.assign({}, props, {
      options: options,
      searchable: false,
      open: isOpen,
      onOpenChange: handleOpenChange,
      selectedItemsPlaceholder: selectedItemsPlaceholder,
      selectedValues: value ? [value] : undefined,
      onSelectedOptionChange: handleSelectedOptionChange,
      prefixWithSelectedItemsPlaceholder: prefixWithSelectedItemsPlaceholder
    }))]
  });
};
export default VoiceSelectWithPreview;